import { add, qs, qsa, remove } from "@fluorescent/dom";
import { Ajaxinate } from "ajaxinate";

import { section } from "@/js/glow";
import atBreakpointChange from "@/js/lib/at-breakpoint-change";
import getMediaQuery from "@/js/lib/media-queries";
import ProductItem from "@/js/lib/product-item";
import filterHandler from "@/js/lib/filters/filter-handler";
import filterDrawer from "@/js/lib/filters/filter-drawer";
import filterBar from "@/js/lib/filters/filter-bar";
import filterSidebar from "@/js/lib/filters/filter-sidebar";
import { default as stickyScroll } from "@/js/lib/sticky-scroll";
import { emit } from "@/js/glow/events";
import { animateCollection, shouldAnimate } from "@/js/lib/animation";

import dispatchCustomEvent from "@/js/lib/dispatch-custom-event";

const selectors = {
  infiniteScrollContainer: ".collection__infinite-container",
  infiniteScrollTrigger: ".collection__infinite-trigger",
  partial: "[data-partial]",
  flyouts: "[data-filter-modal]",
  filterDrawer: "[data-filter-drawer]",
  filterBar: "[data-filter-bar]",
  filterSidebar: "[data-filter-sidebar]",
  loader: ".collection__loading",
  paginationItemCount: "[data-pagination-item-count]",
  productItems: ".product-item:not(.product-item--info-card)",
  infoCards: ".collection--info-cards .product-item--info-card",
  productTitle: ".product-item__product-title",
  collectionSoldOutBottom: "[data-collection-products-sold-out-to-bottom]",
  sortByChecked: "[data-sort-item-input][checked]",
  sortLabel: "[data-sort-label]",
  paginationTotal: "[data-pagination]",
  availability: "filter.v.availability",
  resultsCount: "[data-results-count]",
  storageKeyResults: "collection-results-count",
  paginationList: ".pagination__list",
  btnAddCart: '[data-add-to-cart]',
};

const classes = {
  active: "active",
  isActive: "is-active",
  hideProducts: "animation--collection-products-hide",
};

const { strings } = window.theme;

section("collection", {
  infiniteScroll: null,

  onLoad() {
    const { collectionItemCount, paginationType } = this.container.dataset;

    if (!parseInt(collectionItemCount)) return;

    this.flyouts = qsa(selectors.flyouts, this.container);
    this.filterDrawerEl = qs(selectors.filterDrawer, this.container);
    this.filterbarEl = qs(selectors.filterBar, this.container);
    this.paginationItemCount = qs(selectors.paginationItemCount, this.container);
    this.paginationTotalPages = Math.ceil(Number(this.container.dataset?.collectionItemCount) / Number(this.container.dataset?.collectionItemsPerPage))

    if (this.filterDrawerEl || this.filterbarEl) {
      this.partial = qs(selectors.partial, this.container);

      this.filterDrawer = filterDrawer(this.container);
      this.filterBar = filterBar(this.container);
      this.filterSidebar = filterSidebar(this.container);
      this.filterHandler = filterHandler({
        container: this.container,
        renderCB: this._renderView.bind(this),
      });

      if (
        this.filterSidebar &&
        this.container.dataset.enableStickyFilterSidebar === "true"
      ) {
        this.mobileQuery = window.matchMedia(getMediaQuery("below-960"));
        this._initStickyScroll();
        this.breakPointHandler = atBreakpointChange(960, () => {
          this._initStickyScroll();
          this._closeDrawerDesktop();
        });
      }
    }

    // Infinite scroll
    this.paginationType = paginationType;
    this.paginated = this.paginationType === "paginated";

    this.infiniteScrollTrigger = qs(
      selectors.infiniteScrollTrigger,
      this.container
    );

    if (!this.paginated) {
      this._initInfiniteScroll();
    }

    this.productItem = ProductItem(this.container);

    if (shouldAnimate(this.container)) {
      this.animateCollection = animateCollection(this.container);
    }

    this.paginatedProducts = []
    this.perPage = this.container.dataset?.collectionItemsPerPage || 16
    this.collectionSoldOutBottom = qs(selectors.collectionSoldOutBottom, this.container)
    this.searchParams = new URLSearchParams(window.location.search)
    this.currentPageNumber = !this.searchParams.get('page') ? 1 : this.searchParams.get('page')
    this.storageResults = localStorage.getItem(selectors.storageKeyResults);
    this.paginationList = qs(selectors.paginationList, this.container)
    this.paginationEl = qs(selectors.paginationTotal, this.container)

    if (this.collectionSoldOutBottom) {
      this._renderViewSortProducts()

      document.addEventListener('collection:reload', (e) => {
          this._renderViewSortProducts()
        },
        false,
      );
    } else {
      if (this.storageResults) localStorage.removeItem(selectors.storageKeyResults)
    }

    this._initSortByValue()
  },

  _initStickyScroll() {
    if (this.mobileQuery.matches) {
      if (this.stickyScroll) {
        this.stickyScroll.destroy()
        this.stickyScroll = null
      }
    } else if (!this.stickyScroll) {
      this.stickyScroll = stickyScroll(this.container)
    }
  },

  _initInfiniteScroll() {
    const infiniteScrollOptions = {
      container: selectors.infiniteScrollContainer,
      pagination: selectors.infiniteScrollTrigger,
      loadingText: "Loading...",
      callback: () => {
        this.productItem && this.productItem.unload();
        this.productItem = ProductItem(this.container);
        this.animateCollection?.infiniteScrollReveal();
        this._updatePaginationCount();

        emit("collection:updated");
      },
    };

    if (this.paginationType === "click") {
      infiniteScrollOptions.method = "click";
    }

    this.infiniteScroll = new Ajaxinate(infiniteScrollOptions);
  },

  _closeDrawerDesktop() {
    if (window.innerWidth >= 960) {
      remove(this.flyouts, classes.active);
      remove(this.filterDrawerEl, classes.active);
    }
  },

  _updatePaginationCount() {
    const productItemCount = qsa(selectors.productItems, this.container).length;
    const viewing = strings.pagination.viewing
      .replace("{{ of }}", `1-${productItemCount}`)
      .replace("{{ total }}", this.partial.dataset.collectionProductsCount);
    if (this.paginationItemCount) {
      this.paginationItemCount.innerHTML = `${viewing} ${strings.pagination.products}`;
    }
  },

  _renderView(searchParams, updateHistory = true) {
    const url = `${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${searchParams}`;
    const loading = qs(selectors.loader, this.container);

    add(this.partial, classes.hideProducts);
    add(loading, classes.isActive);

    fetch(url)
      .then(res => res.text())
      .then(async (res) => {
        if (updateHistory) {
          this._updateURLHash(searchParams);
        }

        const pageParams = new URLSearchParams(window.location.search)
        const pageNumber = !pageParams.get('page') ? 1 : pageParams.get('page')
        const doc = new DOMParser().parseFromString(res, "text/html");
        const updatedPartial = qs(selectors.partial, doc);

        if (this.collectionSoldOutBottom) {
          this.collectionSoldOutBottom = qs(selectors.collectionSoldOutBottom, this.container)
          await this._renderViewSortProducts(pageNumber, doc);
        } else {
          this.partial.dataset.collectionProductsCount = updatedPartial.dataset.collectionProductsCount;
          this.partial.innerHTML = updatedPartial.innerHTML;
        }

        this.animateCollection?.updateContents();

        if (!this.paginated && this.infiniteScrollTrigger) {
          this.infiniteScrollTrigger.innerHTML = "";

          this._initInfiniteScroll();
        }

        this.filterDrawer && this.filterDrawer.renderFilters(doc);
        this.filterBar && this.filterBar.renderFilters(doc);
        this.filterSidebar && this.filterSidebar.renderFilters(doc);
        if (!this.collectionSoldOutBottom) {
          this.productItem && this.productItem.unload();
          this.productItem = ProductItem(this.container);
        }
        this.paginationItemCount = qs(selectors.paginationItemCount, this.container);

        if (!this.collectionSoldOutBottom) {
          remove(loading, classes.isActive);
          emit("collection:updated");
        }
      });
  },

  _renderViewSortProducts(searchParams) {
    this.searchParams = new URLSearchParams(window.location.search)
    this.currentPageNumber = !this.searchParams.get('page') ? 1 : this.searchParams.get('page')
    this.animateCollection = animateCollection(this.container);

    const pageCollections = this.collectionSoldOutBottom
    const loading = qs(selectors.loader, this.container)
    let pageNumber = Number(!searchParams ? this.currentPageNumber : searchParams)
    let storageResults = localStorage.getItem(selectors.storageKeyResults)

    add(this.partial, classes.hideProducts)
    add(loading, classes.isActive)

    if (storageResults) {
      localStorage.removeItem(selectors.storageKeyResults)
    }

    this._initHandleSortProducts().then(res => {
      if (res) {
        const resultsCountEl = qs(selectors.resultsCount, this.container)
        storageResults = localStorage.getItem(selectors.storageKeyResults)

        if (storageResults) {
          const resultsJson = JSON.parse(storageResults)
          const { results, paginated } = resultsJson

          if (resultsCountEl && resultsJson.results !== resultsCountEl.dataset.resultsCount) {
            let resultsLabel = 'No results'

            if (results > 0) {
              resultsLabel = `${results} ${results === 1 ? 'Result' : 'Results'}`
            }

            resultsCountEl.setAttribute('data-results-count', results)
            resultsCountEl.innerHTML = `${resultsLabel}`
          }

          if (paginated > pageNumber || paginated < pageNumber) {
            pageNumber = 1
            this.currentPageNumber = pageNumber
            this.searchParams.set('page', this.currentPageNumber)
            this._updateURLHash(this.searchParams.toString());
          }

          this.partial.dataset.collectionProductsCount = resultsJson.results
          this._createPagination(Number(this.currentPageNumber), Number(paginated))
        }

        const productsByPage = this._getProductsByPage(pageNumber)

        if (productsByPage) {
          pageCollections.innerHTML = ''
          productsByPage.forEach((item) => {
            item.removeAttribute('data-available')
            item.removeAttribute('data-date')

            pageCollections.innerHTML += item.outerHTML
          })

          this.animateCollection?.updateContents()
          this.productItem && this.productItem.unload()
          this.productItem = ProductItem(this.container, true)

          this._updatePaginationCountSoldOutBottom(productsByPage.length)

          remove(loading, classes.isActive)
          emit("collection:updated")

          dispatchCustomEvent("collection:loaded")
          dispatchCustomEvent("rebuy:smartcart.ready")
        }
      }
    })
  },

  async _initHandleSortProducts() {
    const sortByChecked = qs(selectors.sortByChecked, this.container)
    const paginationTotal = this.paginationTotalPages || 1
    const pageParams = new URLSearchParams(window.location.search)
    const availability = pageParams.get(selectors.availability)
    const sortByParam = pageParams.get('sort_by')
    const sortByValue = sortByParam ? sortByParam : sortByChecked.value

    if (sortByChecked && !sortByParam) {
      pageParams.append('sort_by', sortByChecked.value)
    }

    const fetchItems = async (params, availableStatus) => {
      let items = []
      let infoCardItems = []
      const promises = []

      for (let page = 1; page <= paginationTotal; page++) {
        params.set('page', page)

        promises.push(
          fetch(`${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${params.toString()}`)
            .then(response => response.text())
            .then(text => {
              const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector(selectors.collectionSoldOutBottom)
              const pageItems = qsa(selectors.productItems, sectionInnerHTML)

              pageItems.forEach(item => {
                if (item.dataset.available === availableStatus) {
                  items.push(item)
                }
              });

              infoCardItems = [...qsa(selectors.infoCards, new DOMParser().parseFromString(text, 'text/html'))]
            })
        );
      }

      await Promise.all(promises)
      return [items, infoCardItems];
    };

    let inStockItems = []
    let outStockItems = []
    let infoCardItems = []

    if (availability === '1') {
      [inStockItems, infoCardItems] = await fetchItems(pageParams, 'true')
    } else if (availability === '0') {
      [outStockItems, infoCardItems] = await fetchItems(pageParams, 'false')
    } else {
      const allParams = new URLSearchParams(window.location.search)

      for (let page = 1; page <= paginationTotal; page++) {
        allParams.set('page', page);
        const response = await fetch(`${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${allParams.toString()}`)
        const text = await response.text()
        const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector(selectors.collectionSoldOutBottom)
        const pageItems = qsa(selectors.productItems, sectionInnerHTML)

        pageItems.forEach(item => {
          if (item.dataset.available === 'true') {
            inStockItems.push(item)
          } else {
            outStockItems.push(item)
          }
        });

        infoCardItems = [...qsa(selectors.infoCards, new DOMParser().parseFromString(text, 'text/html'))]
      }
    }

    inStockItems = this._sortByProduct(sortByValue, inStockItems)
    outStockItems = this._sortByProduct(sortByValue, outStockItems)
    const products = [...inStockItems, ...outStockItems]

    infoCardItems.sort((a, b) => {
      if (Number(a.dataset.position) > Number(b.dataset.position)) {
        return 0;
      } else if (Number(a.dataset.position) < Number(b.dataset.position)) {
        return 1;
      } else {
        return -1;
      }
    });

    let added = 0
    let i = 0
    if (infoCardItems.length > 0) {
      while (products[i] !== undefined || (products[i] === undefined && products[i - 1] !== undefined)) {
        for (const item of infoCardItems) {
          if (Number(item.dataset.position - 1) === i) {
            products.splice(i, 0, item)
            added++
          }
        }
        i++;
      }
    }

    this.paginatedProducts = []
    for (let i = 0; i < products.length; i += Number(this.perPage)) {
      this.paginatedProducts.push(products.slice(i, i + Number(this.perPage)))
    }

    localStorage.setItem(selectors.storageKeyResults, JSON.stringify({
      inStock: inStockItems.length,
      outStock: outStockItems.length,
      results: products.length,
      paginated: this.paginatedProducts.length
    }));

    return true
  },

  _sortByProduct(sortByValue, product) {
    if (sortByValue) {
      switch (sortByValue) {
        case 'price-ascending':
          product.sort((a, b) => parseFloat(a.dataset?.price) - parseFloat(b.dataset?.price))
          break;
        case 'price-descending':
          product.sort((a, b) => parseFloat(b.dataset?.price) - parseFloat(a.dataset?.price))
          break;
        case 'title-ascending':
          product.sort((a, b) => {
            const aTitle = qs(selectors.productTitle, a)
            const bTitle = qs(selectors.productTitle, b)

            return aTitle.innerText.localeCompare(bTitle.innerText)
          });
          break;
        case 'title-descending':
          product.sort((a, b) => {
            const aTitle = qs(selectors.productTitle, a)
            const bTitle = qs(selectors.productTitle, b)

            return bTitle.innerText.localeCompare(aTitle.innerText)
          });
          break;
        case 'created-ascending':
          product.sort((a, b) => new Date(b.dataset.date) - new Date(a.dataset.date))
          break;
        case 'created-descending':
          product.sort((a, b) => new Date(a.dataset.date) - new Date(b.dataset.date))
          break;
        default:
          break;
      }

      return product
    }
  },

  _getProductsByPage(pageNumber) {
    return this.paginatedProducts[pageNumber - 1] || []
  },

  _createPagination(currentPage, totalPages) {
    if (this.paginationEl && totalPages < 2) {
      this.paginationEl.classList.add('hidden')
    } else {
      this.paginationEl.classList.remove('hidden')
    }

    let paginationIcon = qs('.pagination__sold-out-to-bottom', this.container)
    paginationIcon = paginationIcon ? paginationIcon.innerHTML : ''

    let liTag = ''
    let prevPage = currentPage > 1 ? currentPage - 1 : currentPage
    let nextPage = currentPage < totalPages ? currentPage + 1 : currentPage
    let beforePage = currentPage - 1 <= 0 ? 1 : currentPage - 1
    let afterPage = currentPage + 1
    let activeClass = ''
    const btnClass = 'pagination__navigation-button ff-body fs-body-25 text-sm leading-[1.25] font-normal'
    const itemEllipsis = `<li class="pagination__item pagination__item--ellip"><span class="${btnClass}">...</span></li>`

    liTag += '<div class="pagination__item pagination__item--arrow">'
    liTag += `<a href="#" class="pagination__navigation-button pagination__navigation-button--previous${currentPage === 1 ? ' disabled' : ''}" aria-label="Previous page of products" data-pagi="${prevPage}" ${currentPage === 1 ? 'aria-hidden="true"' : ''}">${paginationIcon}</a>`
    liTag += '</div>'

    liTag += '<ul class="pagination__list">'

    if (currentPage > 2 && totalPages > 5) {
      liTag += `<li class="pagination__item">`
      liTag += `<a href="#" class="${btnClass}" aria-label="Page 1 of products" data-pagi="1">1</a>`
      liTag += '</li>'
      if (currentPage > 3) {
        liTag += itemEllipsis
      }
    }

    if (currentPage === totalPages) {
      beforePage = beforePage - 2 < 0 ? 1 : beforePage - 2
    } else if (currentPage === totalPages - 1) {
      beforePage = beforePage - 1 < 0 ? 1 : beforePage - 1
    }

    if (currentPage === 1) {
      afterPage = afterPage + 2
    } else if (currentPage === 2) {
      afterPage = afterPage + 1
    }

    for (let plength = beforePage; plength <= afterPage; plength++) {
      if (plength > totalPages) {
        continue
      }

      if (plength === 0) {
        plength = plength + 1
      }

      if (currentPage === plength) {
        activeClass = ' pagination__item--active'
      } else {
        activeClass = ''
      }

      liTag += `<li class="pagination__item${activeClass}">`
      liTag += `<a href="#" class="${btnClass}" aria-label="Page ${plength} of products" data-pagi="${plength}">${plength}</a>`
      liTag += '</li>'
    }

    if (currentPage < totalPages - 1 && totalPages - 1 >= 4) {
      if (currentPage < totalPages - 2 && totalPages - 1 >= 5) {
        liTag += itemEllipsis
      }

      liTag += `<li class="pagination__item">`
      liTag += `<a href="#" class="${btnClass}" aria-label="Page ${totalPages} of products" data-pagi="${totalPages}">${totalPages}</a>`
      liTag += '</li>'
    }

    liTag += '</ul>'

    liTag += '<div class="pagination__item pagination__item--arrow">'
    liTag += `<a href="#" class="pagination__navigation-button pagination__navigation-button--next${currentPage === totalPages ? ' disabled' : ''}" aria-label="Next page of products" data-pagi="${nextPage}" ${currentPage === totalPages ? 'aria-hidden="true"' : ''}>${paginationIcon}</a>`
    liTag += '</div>'

    if (this.paginationEl) {
      this.paginationEl.childNodes[0].innerHTML = liTag
      this._handleClickPagination()
    }
  },

  _handleClickPagination() {
    const btnPagination = qsa('.pagination__navigation-button', this.container)

    btnPagination.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault()

        this.currentPageNumber = Number(button.dataset.pagi)

        const contentProducts = this._getProductsByPage(this.currentPageNumber)
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set('page', this.currentPageNumber)

        this._updateURLHash(searchParams.toString());
        this._createPagination(this.currentPageNumber, this.paginatedProducts.length)

        if (contentProducts) {
          this.collectionSoldOutBottom.innerHTML = ''

          contentProducts.forEach((item) => {
            item.removeAttribute('data-available')
            item.removeAttribute('data-date')

            this.collectionSoldOutBottom.innerHTML += item.outerHTML
          })

          this._updatePaginationCountSoldOutBottom(contentProducts.length)

          this.animateCollection?.updateContents()
          this.productItem && this.productItem.unload()
          this.productItem = ProductItem(this.container, true)

          dispatchCustomEvent("collection:loaded")
          dispatchCustomEvent("rebuy:smartcart.ready")
        }
      })
    })
  },

  _updatePaginationCountSoldOutBottom(totalProductsByPage) {
    const currentPage = this.currentPageNumber >= 1 ? this.currentPageNumber - 1 : 1
    const startProduct = currentPage * this.perPage === 0 ? 1 : currentPage * this.perPage
    const endProduct = currentPage * this.perPage + totalProductsByPage
    const viewing = strings.pagination.viewing
      .replace("{{ of }}", `${startProduct}-${endProduct}`)
      .replace("{{ total }}", this.partial.dataset.collectionProductsCount)

    if (this.paginationItemCount) {
      this.paginationItemCount.innerHTML = `${viewing} ${strings.pagination.products}`
    }
  },

  _initSortByValue() {
    const sortLabelEl = qs(selectors.sortLabel, this.container);
    const sortByChecked = qs(selectors.sortByChecked, this.container)

    if (sortLabelEl && sortByChecked) {
      sortLabelEl.innerHTML = sortByChecked.nextElementSibling.textContent
    }
  },

  _updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  },

  onUnload() {
    this.infiniteScroll && this.infiniteScroll.destroy();
    this.filterHandler && this.filterHandler.unload();

    this.filterDrawer && this.filterDrawer.unload();
    this.filterBar && this.filterBar.unload();
    this.filterSidebar && this.filterSidebar.unload();
    this.filtering && this.filtering.unload();

    this.productItem && this.productItem.unload();

    this.animateCollection?.destroy();
  },
});